import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Suspense, useMemo } from 'react';

import { AuthProvider } from '@/lib/auth';
import { ActiveClinicProvider } from '@/lib/ActiveClinicContext';
import { ThemeProvider } from '@/lib/theme';
import { createQueryClient } from '@/lib/queryClient';

import { ProtectedRoute } from '@/components/ProtectedRoute';
import { PermissionRoute } from '@/components/PermissionRoute';
import { MainLayout } from '@/components/Layout/MainLayout';
import { Toaster } from '@/components/ui/Toaster';
import { SessionManager } from '@/components/session/SessionManager';

import { SignIn } from '@/pages/auth/SignIn';
import { SignUp } from '@/pages/auth/SignUp';
import { CreateClinic } from '@/pages/auth/CreateClinic';
import { ForgotPassword } from '@/pages/auth/ForgotPassword';
import { ResetPassword } from '@/pages/auth/ResetPassword';
import { AcceptInvitation } from '@/pages/auth/AcceptInvitation';

import { Dashboard } from '@/pages/dashboard/Dashboard';
import { Clinics } from '@/pages/clinics/Clinics';
import { Patients } from '@/pages/patients/Patients';
import { NewPatient } from '@/pages/patients/NewPatient';
import { PatientDetail } from '@/pages/patients/PatientDetail';
import { KommunikationInboxRedirect, PatientMessagesInbox } from '@/pages/patients/PatientMessagesInbox';
import { DentalModulePage } from '@/pages/dental/DentalModulePage';
import { Appointments } from '@/pages/appointments/Appointments';
import { Tasks } from '@/pages/tasks/Tasks';
import { Economy } from '@/pages/economy/Economy';
import { Invoices } from '@/pages/invoices/Invoices';
import { NewInvoice } from '@/pages/invoices/NewInvoice';
import { InvoiceDetail } from '@/pages/invoices/InvoiceDetail';
import { Payments } from '@/pages/payments/Payments';
import { PaymentSuccess } from '@/pages/payments/PaymentSuccess';
import { Sms } from '@/pages/sms/Sms';
import { Quotes } from '@/pages/quotes/Quotes';
import { NewQuote } from '@/pages/quotes/NewQuote';
import { QuoteDetail } from '@/pages/quotes/QuoteDetail';
import { ReportsHome } from '@/pages/economy/reports/ReportsHome';
import { PractitionerReportPage } from '@/pages/economy/reports/PractitionerReportPage';
import { CashierReportPage } from '@/pages/economy/reports/CashierReportPage';
import { PaymentQueuePage } from '@/pages/economy/PaymentQueuePage';
import { Referrals } from '@/pages/referrals/Referrals';
import { NewReferral } from '@/pages/referrals/NewReferral';
import { ReferralDetail } from '@/pages/referrals/ReferralDetail';
import { CareCommunications } from '@/pages/communicate/CareCommunications';
import { MedbinAttachment } from '@/pages/communicate/MedbinAttachment';
import {
  ForwardCareCommunication,
  NewCareCommunication,
  ReplyCareCommunication,
} from '@/pages/communicate/ComposeCareCommunication';
import { CareCommunicationThread } from '@/pages/communicate/CareCommunicationThread';
import { MedComCertification } from '@/pages/medcom/MedComCertification';
import { Standarder } from '@/pages/medcom/Standarder';
import { LabAnalysisCatalogue } from '@/pages/medcom/LabAnalysisCatalogue';
import {
  LegacyCareCommunicationsRedirect,
  MedcomIndex,
  MedcomSection,
} from '@/pages/medcom/MedcomSection';
import { AcceptQuote } from '@/pages/quotes/AcceptQuote';
import { PayInvoice } from '@/pages/payInvoice/PayInvoice';
import { Settings } from '@/pages/settings/Settings';
import { SettingsLayout } from '@/components/Layout/SettingsLayout';
import { ClinicGeneral } from '@/pages/settings/ClinicGeneral';
import { ClinicBranding } from '@/pages/settings/ClinicBranding';
import { ClinicHours } from '@/pages/settings/ClinicHours';
import { ClinicServices } from '@/pages/settings/ClinicServices';
import { TreatmentCourseEditorPage } from '@/pages/settings/TreatmentCourseEditorPage';
import { ClinicResources } from '@/pages/settings/ClinicResources';
import { ClinicUsers } from '@/pages/settings/ClinicUsers';
import { ClinicUserDetail } from '@/pages/settings/ClinicUserDetail';
import { ClinicRoles } from '@/pages/settings/ClinicRoles';
import { ClinicBilling } from '@/pages/settings/ClinicBilling';
import { JournalTemplates } from '@/pages/settings/JournalTemplates';
import { JournalPhrases } from '@/pages/settings/JournalPhrases';
import { ClinicContacts } from '@/pages/settings/ClinicContacts';
import { PatientFileCategories } from '@/pages/settings/PatientFileCategories';
import { PunchCardTemplates } from '@/pages/settings/PunchCardTemplates';
import { ClinicSmsAutomation } from '@/pages/settings/ClinicSmsAutomation';
import { ClinicPayments } from '@/pages/settings/ClinicPayments';
import { ClinicSygeforsikring } from '@/pages/settings/ClinicSygeforsikring';
import { ClinicOnlineBooking } from '@/pages/settings/ClinicOnlineBooking';
import { QuestionnaireSettings } from '@/pages/settings/QuestionnaireSettings';
import { ClinicIntegrations } from '@/pages/settings/ClinicIntegrations';
import { ClinicCommunicate } from '@/pages/settings/ClinicCommunicate';
import { ClinicWhereby } from '@/pages/settings/ClinicWhereby';
import { ClinicPatientReports } from '@/pages/settings/ClinicPatientReports';
import { ClinicEmail } from '@/pages/settings/ClinicEmail';
import { ClinicConsentTexts } from '@/pages/settings/ClinicConsentTexts';
import { SmsSettingsLayout } from '@/pages/settings/SmsSettingsLayout';
import { AuditLogs } from '@/pages/settings/AuditLogs';
import { ClinicsClinics } from '@/pages/settings/ClinicsClinics';
import { SubClinicDetail } from '@/pages/settings/SubClinicDetail';
import { BookingFlow } from '@/pages/booking/BookingFlow';
import { AccountSecurity } from '@/pages/account/Security';
import { AccountSessions } from '@/pages/account/Sessions';
import { NotFound } from '@/pages/NotFound';

import { PortalLogin } from '@/pages/portal/PortalLogin';
import { PortalAcceptInvitation } from '@/pages/portal/PortalAcceptInvitation';
import { PortalLayout } from '@/pages/portal/PortalLayout';
import { PortalConsents } from '@/pages/portal/PortalConsents';
import { PortalDashboard } from '@/pages/portal/PortalDashboard';
import { PortalClinics } from '@/pages/portal/PortalClinics';
import { PortalProfile } from '@/pages/portal/PortalProfile';
import { PortalAppointments } from '@/pages/portal/PortalAppointments';
import { PortalMessages } from '@/pages/portal/PortalMessages';
import { PortalDocuments } from '@/pages/portal/PortalDocuments';
import { PortalLabResults } from '@/pages/portal/PortalLabResults';
import { PortalInvoices } from '@/pages/portal/PortalInvoices';
import { PortalQuestionnaires } from '@/pages/portal/PortalQuestionnaires';
import { PortalPunchCards } from '@/pages/portal/PortalPunchCards';
import { PortalTreatmentCourses } from '@/pages/portal/PortalTreatmentCourses';
import { PortalTreatmentCourseDetail } from '@/pages/portal/PortalTreatmentCourseDetail';
import { PortalDataExport } from '@/pages/portal/PortalDataExport';
import { PortalMore } from '@/pages/portal/PortalMore';
import { PortalProtectedRoute } from '@/components/portal/PortalProtectedRoute';

import { QuestionnaireRedirect } from '@/pages/questionnaire/QuestionnaireRedirect';
import { QuestionnaireThankYou } from '@/pages/questionnaire/QuestionnaireThankYou';

import { SubProcessors } from '@/pages/legal/SubProcessors';

import { HelpCenter } from '@/pages/help/HelpCenter';
import { HelpCategory } from '@/pages/help/HelpCategory';
import { HelpArticle } from '@/pages/help/HelpArticle';

import { AdminLayout } from '@/components/Layout/AdminLayout';
import { AdminDashboard } from '@/pages/admin/AdminDashboard';
import { AdminStripeSettings } from '@/pages/admin/AdminStripeSettings';
import { AdminBilling } from '@/pages/admin/AdminBilling';
import { AdminClinics } from '@/pages/admin/AdminClinics';
import { AdminModules } from '@/pages/admin/AdminModules';
import { AdminSms } from '@/pages/admin/AdminSms';
import { AdminUsers } from '@/pages/admin/AdminUsers';
import { AdminSettings } from '@/pages/admin/AdminSettings';
import { Integrations } from '@/pages/admin/Integrations';

import { isDevelopment } from '@/lib/env';

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-ds-bg">
      <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-ds-teal" />
    </div>
  );
}

function TreatmentCourseLegacyRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/settings/services/course/${id}` : '/settings/services'} replace />;
}

export default function App() {
  const queryClient = useMemo(() => createQueryClient(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <ActiveClinicProvider>
              <Suspense fallback={<FullPageSpinner />}>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />

                  {/* Public auth routes */}
                  <Route path="/sign-in" element={<SignIn />} />
                  <Route path="/sign-up" element={<SignUp />} />
                  <Route path="/create-clinic" element={<CreateClinic />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/accept-invitation" element={<AcceptInvitation />} />
                  <Route path="/accept-quote/:token" element={<AcceptQuote />} />
                  <Route path="/pay-invoice/:token" element={<PayInvoice />} />
                  {/* PAY-14: patient-facing receipt after Clinio Pay checkout */}
                  <Route path="/payment-success" element={<PaymentSuccess />} />
                  {/* PAY-6: /book/:clinicSlug uses Clinio Pay BookingFlow (replaces legacy PublicBooking) */}
                  <Route path="/book/:clinicSlug" element={<BookingFlow />} />
                  {/* FORMS-5: /questionnaire/:token (legacy) replaced by /spoergeskemaer/:token (redirects to forms.clinio.app/q/<token>) */}
                  <Route path="/spoergeskemaer/tak" element={<QuestionnaireThankYou />} />
                  <Route path="/spoergeskemaer/:token" element={<QuestionnaireRedirect />} />

                  {/* Public legal routes (SHARED-4 / CLINIO-194) */}
                  <Route path="/legal/sub-processors" element={<SubProcessors />} />

                  {/* Patient portal routes */}
                  <Route path="/portal/login" element={<PortalLogin />} />
                  <Route path="/portal/accept-invitation" element={<PortalAcceptInvitation />} />
                  <Route path="/portal/reset-password" element={<ResetPassword />} />
                  <Route
                    path="/portal"
                    element={
                      <PortalProtectedRoute>
                        <PortalLayout />
                      </PortalProtectedRoute>
                    }
                  >
                    <Route path="dashboard" element={<PortalDashboard />} />
                    <Route path="messages" element={<PortalMessages />} />
                    <Route path="appointments" element={<PortalAppointments />} />
                    <Route path="treatment-courses" element={<PortalTreatmentCourses />} />
                    <Route
                      path="treatment-courses/:courseId"
                      element={<PortalTreatmentCourseDetail />}
                    />
                    <Route path="documents" element={<PortalDocuments />} />
                    <Route path="lab-results" element={<PortalLabResults />} />
                    <Route path="invoices" element={<PortalInvoices />} />
                    <Route path="punch-cards" element={<PortalPunchCards />} />
                    <Route path="consents" element={<PortalConsents />} />
                    {/* FORMS-5: portal questionnaire list (patient_forms_assignments). */}
                    {/* PortalQuestionnaireDetail + PortalQuestionnaireFill removed —   */}
                    {/* fill-out is now handled by forms.clinio.app (FORMS-5/FORMS-10). */}
                    <Route path="questionnaires" element={<PortalQuestionnaires />} />
                    <Route path="clinics" element={<PortalClinics />} />
                    <Route path="profile" element={<PortalProfile />} />
                    <Route path="data-export" element={<PortalDataExport />} />
                    <Route path="more" element={<PortalMore />} />
                  </Route>

                  {/* Protected app shell */}
                  <Route
                    element={
                      <ProtectedRoute>
                        <MainLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route
                      path="/dashboard"
                      element={
                        <PermissionRoute nav="nav_dashboard">
                          <Dashboard />
                        </PermissionRoute>
                      }
                    />
                    <Route path="/clinics" element={<Clinics />} />
                    <Route
                      path="/patients"
                      element={
                        <PermissionRoute nav="nav_patients" resource="patients">
                          <Patients />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/patients/new"
                      element={
                        <PermissionRoute nav="nav_patients" resource="patients" action="create">
                          <NewPatient />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/patients/messages"
                      element={
                        <PermissionRoute
                          anyOf={['messages', 'communication', 'nav_messages']}
                          moduleKey="patients"
                        >
                          <PatientMessagesInbox />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/patients/kommunikation"
                      element={
                        <PermissionRoute
                          anyOf={['messages', 'communication', 'nav_messages']}
                          moduleKey="patients"
                        >
                          <KommunikationInboxRedirect />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/patients/:id"
                      element={
                        <PermissionRoute nav="nav_patients" resource="patients">
                          <PatientDetail />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/patients/:id/tand"
                      element={
                        <PermissionRoute nav="nav_patients" resource="dental">
                          <DentalModulePage />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/patients/:id/*"
                      element={
                        <PermissionRoute nav="nav_patients" resource="patients">
                          <PatientDetail />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/appointments"
                      element={
                        <PermissionRoute nav="nav_appointments" resource="appointments">
                          <Appointments />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/tasks"
                      element={
                        <PermissionRoute resource="tasks">
                          <Tasks />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/economy"
                      element={
                        <PermissionRoute
                          nav="nav_economy"
                          anyOf={['invoices', 'payments', 'quotes', 'reports']}
                        >
                          <Economy />
                        </PermissionRoute>
                      }
                    >
                      <Route index element={<Navigate to="invoices" replace />} />
                      <Route path="invoices" element={<Invoices embedded />} />
                      <Route
                        path="queue"
                        element={
                          <PermissionRoute resource="invoices">
                            <PaymentQueuePage />
                          </PermissionRoute>
                        }
                      />
                      <Route path="payments" element={<Payments embedded />} />
                      <Route
                        path="quotes"
                        element={
                          <PermissionRoute resource="quotes">
                            <Quotes embedded />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="reports"
                        element={
                          <PermissionRoute resource="reports">
                            <ReportsHome />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="reports/practitioner"
                        element={
                          <PermissionRoute resource="reports">
                            <PractitionerReportPage />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="reports/cashier"
                        element={
                          <PermissionRoute resource="reports">
                            <CashierReportPage />
                          </PermissionRoute>
                        }
                      />
                    </Route>
                    <Route path="/invoices" element={<Navigate to="/economy/invoices" replace />} />
                    <Route
                      path="/invoices/new"
                      element={
                        <PermissionRoute resource="invoices" action="create">
                          <NewInvoice />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/invoices/:id"
                      element={
                        <PermissionRoute resource="invoices">
                          <InvoiceDetail />
                        </PermissionRoute>
                      }
                    />
                    <Route path="/payments" element={<Navigate to="/economy/payments" replace />} />
                    <Route path="/reports" element={<Navigate to="/economy/reports" replace />} />
                    <Route path="/sms" element={<Navigate to="/settings/sms" replace />} />
                    <Route path="/quotes" element={<Navigate to="/economy/quotes" replace />} />
                    <Route
                      path="/quotes/new"
                      element={
                        <PermissionRoute resource="quotes" action="create">
                          <NewQuote />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/quotes/:id"
                      element={
                        <PermissionRoute resource="quotes">
                          <QuoteDetail />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/henvisninger"
                      element={
                        <PermissionRoute
                          nav="nav_referrals"
                          resource="referrals"
                          moduleKey="referrals"
                        >
                          <Referrals />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/henvisninger/new"
                      element={
                        <PermissionRoute
                          nav="nav_referrals"
                          resource="referrals"
                          action="create"
                          moduleKey="referrals"
                        >
                          <NewReferral />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/henvisninger/:id"
                      element={
                        <PermissionRoute
                          nav="nav_referrals"
                          resource="referrals"
                          moduleKey="referrals"
                        >
                          <ReferralDetail />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/medcom"
                      element={
                        <PermissionRoute nav="nav_medcom" resource="medcom" moduleKey="communicate">
                          <MedcomSection />
                        </PermissionRoute>
                      }
                    >
                      <Route index element={<MedcomIndex />} />
                      <Route path="letters" element={<CareCommunications embedded />} />
                      <Route
                        path="letters/new"
                        element={
                          <PermissionRoute
                            nav="nav_medcom"
                            resource="medcom"
                            action="create"
                            moduleKey="communicate"
                          >
                            <NewCareCommunication />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="letters/:communicationIdentifier/reply"
                        element={
                          <PermissionRoute
                            nav="nav_medcom"
                            resource="medcom"
                            action="create"
                            moduleKey="communicate"
                          >
                            <ReplyCareCommunication />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="letters/:communicationIdentifier/forward"
                        element={
                          <PermissionRoute
                            nav="nav_medcom"
                            resource="medcom"
                            action="create"
                            moduleKey="communicate"
                          >
                            <ForwardCareCommunication />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="letters/:communicationIdentifier"
                        element={<CareCommunicationThread />}
                      />
                      <Route path="test" element={<MedComCertification embedded />} />
                      <Route path="attachment" element={<MedbinAttachment embedded />} />
                      <Route path="standarder" element={<Standarder embedded />} />
                      <Route path="analysekatalog" element={<LabAnalysisCatalogue embedded />} />
                    </Route>
                    <Route
                      path="/care-communications"
                      element={<Navigate to="/medcom/letters" replace />}
                    />
                    <Route
                      path="/care-communications/*"
                      element={<LegacyCareCommunicationsRedirect />}
                    />
                    <Route path="/medbin" element={<Navigate to="/medcom/attachment" replace />} />
                    <Route
                      path="/settings"
                      element={
                        <PermissionRoute nav="nav_settings">
                          <Settings />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      element={
                        <PermissionRoute nav="nav_settings">
                          <SettingsLayout />
                        </PermissionRoute>
                      }
                    >
                      <Route
                        path="/settings/general"
                        element={
                          <PermissionRoute resource="settings_general">
                            <ClinicGeneral />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/branding"
                        element={
                          <PermissionRoute resource="settings_general">
                            <ClinicBranding />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/hours"
                        element={
                          <PermissionRoute resource="settings_general">
                            <ClinicHours />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/services"
                        element={
                          <PermissionRoute resource="settings_general">
                            <ClinicServices />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/services/new"
                        element={
                          <PermissionRoute resource="settings_general">
                            <TreatmentCourseEditorPage />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/services/course/:id"
                        element={
                          <PermissionRoute resource="settings_general">
                            <TreatmentCourseEditorPage />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/services/:id"
                        element={
                          <PermissionRoute resource="settings_general">
                            <TreatmentCourseEditorPage />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/treatment-courses"
                        element={<Navigate to="/settings/services" replace />}
                      />
                      <Route
                        path="/settings/treatment-courses/new"
                        element={<Navigate to="/settings/services/new" replace />}
                      />
                      <Route
                        path="/settings/treatment-courses/:id"
                        element={<TreatmentCourseLegacyRedirect />}
                      />
                      <Route
                        path="/settings/resources"
                        element={
                          <PermissionRoute resource="settings_general">
                            <ClinicResources />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/users"
                        element={
                          <PermissionRoute resource="settings_users">
                            <ClinicUsers />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/users/:id"
                        element={
                          <PermissionRoute resource="settings_users">
                            <ClinicUserDetail />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/roles"
                        element={
                          <PermissionRoute resource="settings_roles">
                            <ClinicRoles />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/billing"
                        element={
                          <PermissionRoute resource="settings_billing">
                            <ClinicBilling />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/journal-templates"
                        element={
                          <PermissionRoute resource="journal">
                            <JournalTemplates />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/journal-phrases"
                        element={
                          <PermissionRoute resource="journal_phrases">
                            <JournalPhrases />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/contacts"
                        element={
                          <PermissionRoute resource="patients">
                            <ClinicContacts />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/file-categories"
                        element={
                          <PermissionRoute resource="documents">
                            <PatientFileCategories />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/patient-reports"
                        element={
                          <PermissionRoute resource="settings_general">
                            <ClinicPatientReports />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/punch-card-templates"
                        element={
                          <PermissionRoute resource="punch_cards">
                            <PunchCardTemplates />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/dental-shortcuts"
                        element={<Navigate to="/settings/general" replace />}
                      />
                      <Route
                        path="/settings/integrations"
                        element={
                          <PermissionRoute
                            anyOf={[
                              'settings_general',
                              'payments',
                              'questionnaires',
                              'sms',
                              'sygeforsikring',
                            ]}
                          >
                            <ClinicIntegrations />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/communicate"
                        element={
                          <PermissionRoute resource="settings_general">
                            <ClinicCommunicate />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/consent-texts"
                        element={
                          <PermissionRoute resource="settings_general">
                            <ClinicConsentTexts />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/email"
                        element={
                          <PermissionRoute anyOf={['settings_general', 'communication']}>
                            <ClinicEmail />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/sms"
                        element={
                          <PermissionRoute resource="sms" moduleKey="sms">
                            <SmsSettingsLayout />
                          </PermissionRoute>
                        }
                      >
                        <Route index element={<Sms />} />
                        <Route path="automation" element={<ClinicSmsAutomation />} />
                      </Route>
                      <Route
                        path="/settings/sms-automation"
                        element={<Navigate to="/settings/sms/automation" replace />}
                      />
                      <Route
                        path="/settings/payments"
                        element={
                          <PermissionRoute resource="payments">
                            <ClinicPayments />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/sygeforsikring"
                        element={
                          <PermissionRoute resource="sygeforsikring">
                            <ClinicSygeforsikring />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/whereby"
                        element={
                          <PermissionRoute resource="settings_general">
                            <ClinicWhereby />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/online-booking"
                        element={
                          <PermissionRoute resource="online_booking">
                            <ClinicOnlineBooking />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/questionnaires"
                        element={
                          <PermissionRoute resource="questionnaires">
                            <QuestionnaireSettings />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/clinics"
                        element={
                          <PermissionRoute resource="multi_clinic">
                            <ClinicsClinics />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/clinics/:clinicId"
                        element={
                          <PermissionRoute resource="multi_clinic">
                            <SubClinicDetail />
                          </PermissionRoute>
                        }
                      />
                      <Route
                        path="/settings/audit-logs"
                        element={
                          <PermissionRoute resource="audit_logs">
                            <AuditLogs />
                          </PermissionRoute>
                        }
                      />
                    </Route>
                    <Route path="/account/security" element={<AccountSecurity />} />
                    <Route path="/account/sessions" element={<AccountSessions />} />
                    <Route
                      path="/help"
                      element={
                        <PermissionRoute nav="nav_help">
                          <HelpCenter />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/help/:category"
                      element={
                        <PermissionRoute nav="nav_help">
                          <HelpCategory />
                        </PermissionRoute>
                      }
                    />
                    <Route
                      path="/help/:category/:slug"
                      element={
                        <PermissionRoute nav="nav_help">
                          <HelpArticle />
                        </PermissionRoute>
                      }
                    />
                  </Route>

                  {/* Platform admin routes - separate layout */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute>
                        <AdminLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<AdminDashboard />} />
                    <Route path="stripe" element={<AdminStripeSettings />} />
                    <Route path="billing" element={<AdminBilling />} />
                    <Route path="clinics" element={<AdminClinics />} />
                    <Route path="modules" element={<AdminModules />} />
                    <Route path="sms" element={<AdminSms />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="settings" element={<AdminSettings />} />
                    <Route path="integrations" element={<Integrations />} />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <SessionManager />
              <Toaster />
            </ActiveClinicProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
      {isDevelopment && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
